import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Camera, Loader2 } from "lucide-react";

export const ProfileDialog = ({ open, onClose, t }) => {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/profile/me`).then((r) => setP(r.data)).catch(() => toast.error("Error"));
  }, [open]);

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/uploads/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setP((x) => ({ ...x, photo_url: r.data.url }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload error");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/profile`, {
        name: p.name, email: p.email, phone: p.phone, photo_url: p.photo_url,
      });
      const u = JSON.parse(localStorage.getItem("user") || "{}");
      localStorage.setItem("user", JSON.stringify({ ...u, email: r.data.email, name: r.data.name }));
      toast.success(t("profile.saved"));
      onClose(true);
    } catch (e) {
      toast.error(e?.response?.status === 409 ? t("profile.emailTaken") : (e?.response?.data?.detail || "Error"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="text-gray-900 max-w-sm" data-testid="profile-dialog">
        <DialogHeader><DialogTitle>{t("profile.title")}</DialogTitle></DialogHeader>
        {p && (
          <div className="space-y-3">
            <div className="flex items-center gap-4">
              <button type="button" data-testid="profile-photo-btn" onClick={() => fileRef.current?.click()}
                className="relative w-20 h-20 rounded-full bg-gray-100 border-2 border-gray-200 overflow-hidden flex items-center justify-center hover:border-gray-400 transition-colors shrink-0">
                {p.photo_url
                  ? <img src={p.photo_url} alt="" className="w-full h-full object-cover" />
                  : <Camera className="w-6 h-6 text-gray-400" />}
                {uploading && <div className="absolute inset-0 bg-white/70 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin" /></div>}
              </button>
              <div className="text-xs text-gray-400">{t("profile.uploadHint")}</div>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={uploadPhoto} data-testid="profile-photo-input" />
            </div>
            <div><Label>{t("profile.name")}</Label>
              <Input data-testid="profile-name-input" value={p.name || ""} onChange={(e) => setP({ ...p, name: e.target.value })} /></div>
            <div><Label>Email</Label>
              <Input data-testid="profile-email-input" type="email" value={p.email || ""} onChange={(e) => setP({ ...p, email: e.target.value })} /></div>
            <div><Label>{t("profile.phone")}</Label>
              <Input data-testid="profile-phone-input" value={p.phone || ""} onChange={(e) => setP({ ...p, phone: e.target.value })} placeholder="+33 ..." /></div>
          </div>
        )}
        <DialogFooter>
          <Button data-testid="profile-save-btn" onClick={save} disabled={busy || !p}
            className="bg-gray-900 text-white hover:bg-gray-800 font-semibold w-full">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t("profile.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
